import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X, SlidersHorizontal } from "lucide-react";
import { fetchPrinters, fetchFacets } from "@/lib/api";
import PrinterCard from "@/components/PrinterCard";
import LoadingState from "@/components/LoadingState";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { TYPE_LABELS } from "@/lib/constants";

export default function Reviews() {
    const [params, setParams] = useSearchParams();
    const [printers, setPrinters] = useState([]);
    const [facets, setFacets] = useState({ brands: [], types: [] });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState(params.get("q") || "");
    const [sortBy, setSortBy] = useState("rating");
    const [mobileFilter, setMobileFilter] = useState(false);

    const selectedBrands = params.getAll("brand");
    const selectedTypes = params.getAll("type");

    useEffect(() => {
        fetchFacets().then(setFacets).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        fetchPrinters()
            .then(setPrinters)
            .catch(() => setPrinters([]))
            .finally(() => setLoading(false));
    }, []);

    const toggle = (key, val) => {
        const next = new URLSearchParams(params);
        const existing = next.getAll(key);
        if (existing.includes(val)) {
            next.delete(key);
            existing.filter(v => v !== val).forEach(v => next.append(key, v));
        } else {
            next.append(key, val);
        }
        setParams(next, { replace: true });
    };

    const clearAll = () => {
        setParams({}, { replace: true });
        setQ("");
    };

    const filtered = useMemo(() => {
        let list = [...printers];
        if (selectedBrands.length) list = list.filter(p => selectedBrands.includes(p.brand));
        if (selectedTypes.length) list = list.filter(p => selectedTypes.includes(p.type));
        if (q.trim()) {
            const query = q.trim().toLowerCase();
            list = list.filter(p =>
                p.name.toLowerCase().includes(query) ||
                p.brand.toLowerCase().includes(query) ||
                p.tagline.toLowerCase().includes(query)
            );
        }
        if (sortBy === "rating") list.sort((a, b) => b.rating - a.rating);
        else if (sortBy === "price-asc") list.sort((a, b) => a.price_usd - b.price_usd);
        else if (sortBy === "price-desc") list.sort((a, b) => b.price_usd - a.price_usd);
        else if (sortBy === "newest") list.sort((a, b) => b.year - a.year);
        return list;
    }, [printers, selectedBrands, selectedTypes, q, sortBy]);

    const activeCount = selectedBrands.length + selectedTypes.length + (q ? 1 : 0);

    return (
        <div data-testid="reviews-page" className="container-tight py-16 md:py-24">
            <div className="border-b border-zinc-800 pb-10 mb-10">
                <div className="eyebrow">Reviews</div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mt-3">
                    Every printer, <span className="text-zinc-500">reviewed.</span>
                </h1>
                <p className="text-zinc-400 mt-4 max-w-2xl leading-relaxed">
                    Filter by brand or type. Sort by rating or price. Editorial reviews based on manufacturer specs,
                    published documentation, and independent research.
                </p>
                <p className="mt-3 text-[11px] text-zinc-500 leading-relaxed max-w-2xl" data-testid="reviews-list-disclaimer">
                    Content provided for informational and educational purposes only. All About Printers is not endorsed by or
                    affiliated with any of the brands listed unless explicitly stated.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* Filters */}
                <aside className={`lg:col-span-3 ${mobileFilter ? "block" : "hidden lg:block"}`} data-testid="filter-sidebar">
                    <div className="lg:sticky lg:top-24 space-y-8">
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <div className="eyebrow">Filters</div>
                                {activeCount > 0 && (
                                    <button onClick={clearAll} data-testid="clear-filters" className="text-xs text-blue-400 hover:text-blue-300 transition-colors">Clear ({activeCount})</button>
                                )}
                            </div>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500 pointer-events-none" />
                                <Input
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Search printers…"
                                    data-testid="search-input"
                                    className="bg-zinc-950 border-zinc-800 pl-9 rounded-sm h-10 focus-visible:ring-blue-500"
                                />
                            </div>
                        </div>

                        <div>
                            <div className="eyebrow mb-4">Type</div>
                            <div className="space-y-3">
                                {facets.types.map(t => (
                                    <label key={t} className="flex items-center gap-3 text-sm text-zinc-300 hover:text-white cursor-pointer group">
                                        <Checkbox
                                            checked={selectedTypes.includes(t)}
                                            onCheckedChange={() => toggle("type", t)}
                                            data-testid={`filter-type-${t}`}
                                            className="border-zinc-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 rounded-sm"
                                        />
                                        <span>{TYPE_LABELS[t] || t}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="eyebrow mb-4">Brand</div>
                            <div className="space-y-3">
                                {facets.brands.map(b => (
                                    <label key={b} className="flex items-center gap-3 text-sm text-zinc-300 hover:text-white cursor-pointer">
                                        <Checkbox
                                            checked={selectedBrands.includes(b)}
                                            onCheckedChange={() => toggle("brand", b)}
                                            data-testid={`filter-brand-${b}`}
                                            className="border-zinc-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 rounded-sm"
                                        />
                                        <span>{b}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </aside>

                {/* Grid */}
                <div className="lg:col-span-9">
                    <div className="flex items-center justify-between mb-6">
                        <div className="text-sm text-zinc-400" data-testid="results-count">
                            <span className="text-white font-mono">{filtered.length}</span> of {printers.length} results
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setMobileFilter(v => !v)}
                                data-testid="mobile-filter-toggle"
                                className="lg:hidden inline-flex items-center gap-2 border border-zinc-800 hover:border-zinc-600 px-3 py-2 rounded-sm text-sm text-zinc-300 transition-colors"
                            >
                                <SlidersHorizontal className="h-4 w-4" /> Filters
                            </button>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                data-testid="sort-select"
                                className="bg-zinc-950 border border-zinc-800 hover:border-zinc-600 focus:border-blue-500 focus:outline-none rounded-sm text-sm text-zinc-200 px-3 py-2 transition-colors"
                            >
                                <option value="rating">Highest rated</option>
                                <option value="newest">Newest</option>
                                <option value="price-asc">Price: low to high</option>
                                <option value="price-desc">Price: high to low</option>
                            </select>
                        </div>
                    </div>

                    {loading ? (
                        <LoadingState label="Loading printers" />
                    ) : filtered.length === 0 ? (
                        <div className="card-flat p-12 text-center" data-testid="empty-state">
                            <div className="text-zinc-400">No printers match your filters.</div>
                            <button onClick={clearAll} className="mt-4 text-blue-400 hover:text-blue-300 text-sm">Clear filters</button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="printer-grid">
                            {filtered.map(p => <PrinterCard key={p.id} printer={p} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
